/**
 * User-facing Play load errors. Maps known dog-access API codes to actionable copy.
 * Keeps a generic fallback for true transport/server failures.
 */

export const PLAY_LOAD_GENERIC_UNAVAILABLE =
  'Play is temporarily unavailable. Please try again shortly.'

export const PLAY_LOAD_PREVIEW_UNAVAILABLE =
  'Play read APIs unavailable. Check local database and seed.'

/** Canonical Play Circle dog-access codes from loadOwnedActiveDog / Play routes. */
export const PLAY_DOG_ACCESS_ERROR_CODES = [
  'DOG_NOT_OWNED',
  'DOG_DELETED',
  'GUARDIAN_UNAVAILABLE',
] as const

export type PlayDogAccessErrorCode =
  (typeof PLAY_DOG_ACCESS_ERROR_CODES)[number]

const PLAY_DOG_ACCESS_USER_MESSAGES: Record<PlayDogAccessErrorCode, string> = {
  DOG_NOT_OWNED:
    'This dog is not available for Play on your account. Choose one of your dogs or refresh and try again.',
  DOG_DELETED:
    'This dog is no longer available. Choose another dog to continue with Play.',
  GUARDIAN_UNAVAILABLE:
    'Your account is not eligible for Play right now. Sign out, sign back in, and try again.',
}

export function isPlayDogAccessErrorCode(
  code: string | null | undefined
): code is PlayDogAccessErrorCode {
  return (
    typeof code === 'string' &&
    (PLAY_DOG_ACCESS_ERROR_CODES as readonly string[]).includes(code)
  )
}

/**
 * Map an API error code to user-visible Play load copy.
 * Unknown codes → generic unavailable (not internal messages).
 */
export function mapPlayLoadErrorCodeToUserMessage(
  code: string | null | undefined,
  mode: 'production' | 'preview' = 'production'
): string {
  if (isPlayDogAccessErrorCode(code)) {
    return PLAY_DOG_ACCESS_USER_MESSAGES[code]
  }
  return mode === 'preview'
    ? PLAY_LOAD_PREVIEW_UNAVAILABLE
    : PLAY_LOAD_GENERIC_UNAVAILABLE
}

/**
 * Resolve catch-path UI copy from a thrown Error (message may already be mapped).
 */
export function resolvePlayLoadUserError(
  err: unknown,
  mode: 'production' | 'preview'
): string {
  if (err instanceof Error) {
    const code = (err as Error & { code?: string }).code
    if (isPlayDogAccessErrorCode(code)) {
      return mapPlayLoadErrorCodeToUserMessage(code, mode)
    }
    // Transport may throw with already-mapped user copy as message.
    for (const known of Object.values(PLAY_DOG_ACCESS_USER_MESSAGES)) {
      if (err.message === known) return err.message
    }
  }
  return mode === 'preview'
    ? PLAY_LOAD_PREVIEW_UNAVAILABLE
    : PLAY_LOAD_GENERIC_UNAVAILABLE
}

export function createPlayLoadError(
  code: string | null | undefined,
  mode: 'production' | 'preview' = 'production'
): Error & { code?: string } {
  const message = mapPlayLoadErrorCodeToUserMessage(code, mode)
  const error = new Error(message) as Error & { code?: string }
  if (isPlayDogAccessErrorCode(code)) {
    error.code = code
  }
  return error
}
